<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

import { useCookieConsent } from '../composables/useCookieConsent'
import { reportConsentEvent } from '../utils/consent-events'
import { Button } from './ui/button'

const { t, locale } = useI18n()
const { showConsentBanner, acceptCookies, rejectCookies } = useCookieConsent()

watch(showConsentBanner, (shown) => {
  if (shown)
    reportConsentEvent('shown', locale.value)
}, { immediate: true })

function handleAccept() {
  reportConsentEvent('accepted', locale.value)
  acceptCookies()
  toast.success(t('cookie.accepted'))
}

function handleReject() {
  reportConsentEvent('declined', locale.value)
  rejectCookies()
  toast.info(t('cookie.rejected'))
}
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div
      v-if="showConsentBanner"
      class="fixed bottom-0 left-0 right-0 z-50 border-t border-vis-hairline bg-white shadow-vis-card-lift sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-lg sm:border sm:rounded-vis-lg"
    >
      <div class="p-6">
        <div class="flex items-start gap-4">
          <div class="flex-shrink-0">
            <div class="h-10 w-10 flex items-center justify-center rounded-vis bg-[#9a9ac0]">
              <i class="i-lucide-cookie h-5 w-5 text-white" />
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="mb-2 text-base text-vis-on-light font-semibold tracking-[-0.01em]">
              {{ t('cookie.title') }}
            </h3>
            <p class="mb-2 text-sm text-vis-mid leading-relaxed">
              {{ t('cookie.description') }}
            </p>
            <p class="mb-4 text-xs text-vis-muted leading-relaxed">
              {{ t('cookie.info') }}
            </p>
            <div class="flex flex-col gap-2 sm:flex-row">
              <Button
                data-analytics-id="cookie_consent_accept"
                data-analytics-placement="cookie_banner"
                size="sm"
                class="flex-1 hover:bg-[#9a9ac0] hover:opacity-100"
                @click="handleAccept"
              >
                {{ t('cookie.accept') }}
              </Button>
              <Button
                data-analytics-id="cookie_consent_reject"
                data-analytics-placement="cookie_banner"
                size="sm"
                variant="outline"
                class="flex-1"
                @click="handleReject"
              >
                {{ t('cookie.reject') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
